import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../Layout/Layout";
import { useTitle } from "../../config/global";
import axiosInstance from "../../config/Axios";
import { useTranslation } from "react-i18next";
import "../../styles/honey-ink.css";
import "./AdsSettings.css";
import { useInHub } from '../../nav/useInHub';

/**
 * Ad Settings — rebuilt on Honey & Ink from the approved prototype.
 *
 * ⚠️ THE PAYLOAD IS THE FOUR BOOLEANS, NOT allowed_ad_types. The old page PUT
 * its entire state object back, which included the `allowed_ad_types` array it
 * had received on load. UpdateVenueAdSettingsRequest used to treat the presence
 * of that key as "this caller speaks canonical, leave the booleans alone", so
 * every save echoed a stale array over itself and no toggle ever persisted
 * (backend commit 13/12). The backend no longer trusts the echo, but sending it
 * at all is pointless and re-arms the same footgun, so this page sends only the
 * ten fields it actually owns.
 *
 * ⚠️ RATES COME FROM THE SERVER. `rate_card` + `currency` are served by
 * VenueAdSettingsController::show() out of config/ads.php (backend 14/12) — the
 * same array AdPricingService bills from. The four prices used to be hardcoded
 * display strings here, free to disagree with what advertisers were actually
 * charged. Nothing in this file may retype a price.
 */

/** UI order, and the rate_card key each toggle is priced by. */
const AD_TYPES = [
    { key: "accept_corner_popup",     rate: "corner_popup",     icon: "◱" },
    { key: "accept_audio_only",       rate: "audio_only",       icon: "◉" },
    { key: "accept_silent_visual",    rate: "silent_visual",    icon: "▣" },
    { key: "accept_full_video",       rate: "full_video",       icon: "▶" },
    // Phase 1 shapes. Both default OFF — the backend derives these booleans from
    // allowed_ad_types, and VenueAdSettings::DEFAULT_ALLOWED_TYPES deliberately
    // still holds only the original five, so a venue accepts neither until it
    // says so here. Prices come from the served rate_card, not from this file.
    { key: "accept_bottom_rectangle", rate: "bottom_rectangle", icon: "▬" },
    { key: "accept_l_shape",          rate: "l_shape",          icon: "◰" },
];

// Slider bounds. The backend validates 0..240; the café product intent is one ad
// per six minutes at most, so the control stops at 10. Previously this read
// max={8} while the backend seeded 10, so a fresh venue loaded a value its own
// slider could not represent.
const ADS_PER_HOUR_MIN = 1;
const ADS_PER_HOUR_MAX = 10;

const DEFAULT_SETTINGS = {
    ads_enabled: false,
    accept_corner_popup: true,
    accept_audio_only: true,
    accept_silent_visual: true,
    accept_full_video: true,
    // ⚠️ false, unlike the four above. Those default true because a venue that
    // has never configured anything accepts them (DEFAULT_ALLOWED_TYPES); the
    // Phase 1 shapes are opt-in, and this local default must agree with the
    // server's or the toggle would flash ON before the GET lands.
    accept_bottom_rectangle: false,
    accept_l_shape: false,
    max_ads_per_hour: 5,
    schedule_start_time: "18:00",
    schedule_end_time: "00:00",
    prime_time_enabled: false,
    during_matches_only_corner: false,
};

/** Exactly the keys this page owns. Anything else the GET returns is read-only. */
const OWNED_KEYS = Object.keys(DEFAULT_SETTINGS);

export default function AdsSettings() {
    const { t, i18n } = useTranslation();
  const inHub = useInHub();
    useTitle("Ad Settings");

    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [baseline, setBaseline] = useState(DEFAULT_SETTINGS);
    const [rateCard, setRateCard] = useState(null);
    const [currency, setCurrency] = useState(null);
    /**
     * ⚠️ SEEDED WITH THE CONFIG DEFAULT, NOT WITH NULL OR ZERO. This renders in a
     * sentence about money, so it must never read "You earn %" mid-load nor flash
     * one number and settle on another. 67/33 is what config/ads.php ships, so the
     * first paint already matches what the server is about to confirm — and if the
     * GET fails outright the sentence stays true rather than going blank.
     */
    const [shares, setShares] = useState({ cafe: 67, platform: 33 });
    const [pausedAt, setPausedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    const showToast = useCallback((severity, text) => {
        clearTimeout(toastTimer.current);
        setToast({ severity, text });
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const hydrate = useCallback((data) => {
        const owned = OWNED_KEYS.reduce((acc, k) => {
            acc[k] = data?.[k] ?? DEFAULT_SETTINGS[k];
            return acc;
        }, {});
        setSettings(owned);
        setBaseline(owned);
        setPausedAt(data?.ads_paused_at ?? null);
        if (data?.rate_card) setRateCard(data.rate_card);
        if (data?.currency) setCurrency(data.currency);
        // Served by VenueAdSettingsController from the same revenueSplit() that
        // credits the venue. An older backend omits them and the default stands.
        if (data?.cafe_share_percent != null) {
            setShares({
                cafe: data.cafe_share_percent,
                platform: data.platform_share_percent ?? 100 - data.cafe_share_percent,
            });
        }
    }, []);

    useEffect(() => {
        let alive = true;
        axiosInstance.get("/venue/ad-settings")
            .then(res => { if (alive) hydrate(res.data?.data); })
            .catch(() => { if (alive) showToast("error", t("billing.adSettings.toast.loadFailed")); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [hydrate, showToast, t]);

    const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

    const dirty = useMemo(
        () => OWNED_KEYS.some(k => settings[k] !== baseline[k]),
        [settings, baseline],
    );

    const save = async () => {
        setSaving(true);
        try {
            // Only the owned keys. See the header note on the echo.
            const payload = OWNED_KEYS.reduce((acc, k) => { acc[k] = settings[k]; return acc; }, {});
            await axiosInstance.put("/venue/ad-settings/update", payload);
            setBaseline(settings);
            showToast("success", t("billing.adSettings.toast.saved"));
        } catch {
            showToast("error", t("billing.adSettings.toast.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const resume = async () => {
        // ads_paused_at is deliberately not settable through this form (backend
        // 11.7a): pause/resume is an action with its own endpoint, so that a PUT
        // which happens to omit the field can never resume ads as a side effect.
        // That endpoint does not exist yet — the control is rendered so the state
        // is visible, and reports honestly rather than silently doing nothing.
        showToast("error", t("billing.adSettings.toast.resumeUnavailable"));
    };

    // Retained for the duration-based pause that replaces the hidden .danger-zone
    // section below; it has no caller until that control lands.
    // eslint-disable-next-line no-unused-vars
    const pause = async () => {
        showToast("error", t("billing.adSettings.toast.pauseUnavailable"));
    };

    /**
     * ⚠️ 'ar-EG', NOT 'ar'. Intl resolves a BARE 'ar' to Latin digits ("5") while
     * 'ar-EG' / 'ar-SA' give Arabic-Indic ("٥") — a one-subtag difference with a
     * visible result. LoyaltyAnalytics.jsx:228 already made this exact call, so
     * matching it keeps numerals consistent across the two Arabic surfaces
     * instead of inventing a second convention.
     */
    const num = useCallback(
        (n) => new Intl.NumberFormat(i18n.language === "ar" ? "ar-EG" : "en-US").format(n),
        [i18n.language],
    );

    /** "5 SAR / per 1000" · "٥ ر.س / لكل ألف مشاهدة" — never a hardcoded price. */
    const rateLabel = (rateKey) => {
        const value = rateCard?.[rateKey];
        if (value == null) return null;
        const n = num(value);
        const unit = currency === "SAR"
            ? t("billing.currency.sar")
            : currency;
        return `${n} ${unit} / ${t("billing.per_thousand")}`;
    };

    const saveLabel = saving ? t("billing.adSettings.saving") : t("billing.adSettings.save");

    const fillPct = Math.round(
        ((settings.max_ads_per_hour - ADS_PER_HOUR_MIN) / (ADS_PER_HOUR_MAX - ADS_PER_HOUR_MIN)) * 100,
    );

    if (loading) {
        return (
            <Layout>
                <div className="hi-app">
                    <header className="page-header">
                        <div className="page-header-inner">
                            <div style={{ flex: 1 }}>
                                <div className="skeleton" style={{ height: 30, width: "40%", marginBottom: 10 }} />
                                <div className="skeleton" style={{ height: 14, width: "70%" }} />
                            </div>
                        </div>
                    </header>
                    <main className="main">
                        <div className="hi-card"><div className="hi-card-body">
                            <div className="skeleton" style={{ height: 56 }} />
                        </div></div>
                        <div className="settings-grid">
                            {[0, 1].map(i => (
                                <div className="hi-card" key={i}><div className="hi-card-body">
                                    <div className="skeleton" style={{ height: 18, width: "45%", marginBottom: 16 }} />
                                    <div className="skeleton" style={{ height: 64, marginBottom: 12 }} />
                                    <div className="skeleton" style={{ height: 64, marginBottom: 12 }} />
                                    <div className="skeleton" style={{ height: 64 }} />
                                </div></div>
                            ))}
                        </div>
                    </main>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="hi-app">
                <header className="page-header">
                    <div className="page-header-inner">
                        {!inHub && (
                        <div>
                            <h1 className="page-header-title">{t("billing.adSettings.title")}</h1>
                            <p className="page-header-subtitle">{t("billing.adSettings.subtitle")}</p>
                        </div>
                        )}
                        <div className="page-header-actions">
                            <button
                                type="button"
                                className="hi-btn hi-btn-secondary"
                                onClick={() => setSettings(baseline)}
                                disabled={!dirty || saving}
                            >
                                {t("billing.adSettings.discard")}
                            </button>
                            <button type="button" className="hi-btn hi-btn-primary" onClick={save} disabled={saving}>
                                {saveLabel}
                            </button>
                        </div>
                    </div>
                </header>

                <main className="main">
                    {pausedAt && (
                        <div className="pause-banner">
                            <div className="pause-banner-icon">⏸</div>
                            <div className="pause-banner-content">
                                <div className="pause-banner-title">{t("billing.adSettings.paused.title")}</div>
                                <div className="pause-banner-desc">{t("billing.adSettings.paused.desc")}</div>
                            </div>
                            <button type="button" className="hi-btn hi-btn-primary" onClick={resume}>
                                {t("billing.adSettings.paused.resume")}
                            </button>
                        </div>
                    )}

                    <div className="hi-card reception-card">
                        <div className="reception-card-body">
                            <div className="reception-info">
                                <div className="reception-title">
                                    <span
                                        className="reception-status-dot"
                                        style={settings.ads_enabled ? undefined : { background: "var(--hi-ink-300)" }}
                                    />
                                    {t("billing.adSettings.reception.title")}
                                </div>
                                <div className="reception-desc">{t("billing.adSettings.reception.desc")}</div>
                            </div>
                            <label className="hi-toggle hi-toggle-lg">
                                <input
                                    type="checkbox"
                                    checked={settings.ads_enabled}
                                    onChange={e => set("ads_enabled", e.target.checked)}
                                    aria-label={t("billing.adSettings.reception.title")}
                                />
                                <span className="hi-toggle-slider" />
                            </label>
                        </div>
                    </div>

                    {/* Reception off: say why the grid below is inert, rather than
                        leaving a page of grey controls with no explanation. */}
                    {!settings.ads_enabled && (
                        <div className="hi-inactive-banner">
                            <div className="hi-inactive-banner-icon">◐</div>
                            <div className="hi-inactive-banner-content">
                                <div className="hi-inactive-banner-title">
                                    {t("billing.adSettings.inactive.title")}
                                </div>
                                <div className="hi-inactive-banner-desc">
                                    {t("billing.adSettings.inactive.desc")}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={`settings-grid${settings.ads_enabled ? "" : " is-disabled"}`}>
                        {/* ── Accepted ad types ─────────────────────────────── */}
                        <div className="hi-card">
                            <div className="hi-card-header">
                                <div>
                                    <div className="hi-card-title">{t("billing.adSettings.types.title")}</div>
                                    <div className="hi-card-subtitle">{t("billing.adSettings.types.subtitle")}</div>
                                </div>
                            </div>
                            <div className="hi-card-body">
                                <div className="ad-types-list">
                                    {AD_TYPES.map(type => {
                                        const on = !!settings[type.key];
                                        const rate = rateLabel(type.rate);
                                        return (
                                            <div
                                                key={type.key}
                                                className={`ad-type-item${on && settings.ads_enabled ? " active" : ""}`}
                                            >
                                                <div className="ad-type-icon">{type.icon}</div>
                                                <div className="ad-type-info">
                                                    <div className="ad-type-name">
                                                        {t(`billing.adSettings.types.${type.rate}.name`)}
                                                    </div>
                                                    <div className="ad-type-desc">
                                                        {t(`billing.adSettings.types.${type.rate}.desc`)}
                                                    </div>
                                                    {rate && <span className="ad-type-rate">{rate}</span>}
                                                </div>
                                                <label className="hi-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={on}
                                                        disabled={!settings.ads_enabled}
                                                        onChange={e => set(type.key, e.target.checked)}
                                                        aria-label={t(`billing.adSettings.types.${type.rate}.name`)}
                                                    />
                                                    <span className="hi-toggle-slider" />
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* ── Schedule & frequency ──────────────────────────── */}
                        <div className="hi-card">
                            <div className="hi-card-header">
                                <div>
                                    <div className="hi-card-title">{t("billing.adSettings.schedule.title")}</div>
                                    <div className="hi-card-subtitle">{t("billing.adSettings.schedule.subtitle")}</div>
                                </div>
                            </div>
                            <div className="hi-card-body">
                                <div className="form-field">
                                    <label className="form-label" htmlFor="ads-window-start">
                                        {t("billing.adSettings.schedule.window")}
                                        <span className="form-label-hint">{t("billing.adSettings.schedule.windowHint")}</span>
                                    </label>
                                    <div className="time-input-group">
                                        <input
                                            id="ads-window-start"
                                            type="time"
                                            className="form-input"
                                            value={settings.schedule_start_time ?? ""}
                                            disabled={!settings.ads_enabled}
                                            onChange={e => set("schedule_start_time", e.target.value)}
                                        />
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={settings.schedule_end_time ?? ""}
                                            disabled={!settings.ads_enabled}
                                            onChange={e => set("schedule_end_time", e.target.value)}
                                            aria-label={t("billing.adSettings.schedule.windowHint")}
                                        />
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label className="form-label" htmlFor="ads-per-hour">
                                        {t("billing.adSettings.schedule.maxPerHour")}
                                        <span className="form-label-hint">{t("billing.adSettings.schedule.maxPerHourHint")}</span>
                                    </label>
                                    <div className="slider-field">
                                        <div className="slider-header">
                                            <span style={{ fontSize: 13, color: "var(--hi-text-secondary)" }}>
                                                {t("billing.adSettings.schedule.current")}
                                            </span>
                                            <span className="slider-value">
                                                {t("billing.adSettings.schedule.perHour", { count: num(settings.max_ads_per_hour) })}
                                            </span>
                                        </div>
                                        {/* The visible bar. .hi-range below is
                                            background:transparent with a negative
                                            margin and z-index:1 — it is meant to sit
                                            ON TOP of this, not replace it. */}
                                        <div className="slider-track">
                                            <div className="slider-fill" style={{ width: `${fillPct}%` }} />
                                        </div>
                                        <input
                                            id="ads-per-hour"
                                            type="range"
                                            className="hi-range"
                                            min={ADS_PER_HOUR_MIN}
                                            max={ADS_PER_HOUR_MAX}
                                            step={1}
                                            value={settings.max_ads_per_hour}
                                            disabled={!settings.ads_enabled}
                                            onChange={e => set("max_ads_per_hour", Number(e.target.value))}
                                        />
                                        <div className="slider-labels">
                                            <span>{num(ADS_PER_HOUR_MIN)}</span>
                                            <span>{num(ADS_PER_HOUR_MAX)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label className="form-label">{t("billing.adSettings.schedule.extraRules")}</label>

                                    <div className="setting-row">
                                        <div className="setting-info">
                                            <div className="setting-title">
                                                {t("billing.adSettings.primeTime.title")}
                                                <span className="setting-multiplier">
                                                    {t("billing.adSettings.primeTime.multiplier")}
                                                </span>
                                            </div>
                                            <div className="setting-desc">{t("billing.adSettings.primeTime.desc")}</div>
                                        </div>
                                        <label className="hi-toggle">
                                            <input
                                                type="checkbox"
                                                checked={!!settings.prime_time_enabled}
                                                disabled={!settings.ads_enabled}
                                                onChange={e => set("prime_time_enabled", e.target.checked)}
                                                aria-label={t("billing.adSettings.primeTime.title")}
                                            />
                                            <span className="hi-toggle-slider" />
                                        </label>
                                    </div>

                                    <div className="setting-row">
                                        <div className="setting-info">
                                            <div className="setting-title">{t("billing.adSettings.matchCorner.title")}</div>
                                            <div className="setting-desc">{t("billing.adSettings.matchCorner.desc")}</div>
                                        </div>
                                        <label className="hi-toggle">
                                            <input
                                                type="checkbox"
                                                checked={!!settings.during_matches_only_corner}
                                                disabled={!settings.ads_enabled}
                                                onChange={e => set("during_matches_only_corner", e.target.checked)}
                                                aria-label={t("billing.adSettings.matchCorner.title")}
                                            />
                                            <span className="hi-toggle-slider" />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`earnings-callout${settings.ads_enabled ? "" : " is-disabled"}`}>
                        <div className="earnings-icon">₪</div>
                        <div className="earnings-content">
                            <div className="earnings-title">{t("billing.adSettings.earnings.title")}</div>
                            {/* ⚠️ THE PERCENTAGE IS INTERPOLATED, NOT TYPED INTO THE
                                STRING. It used to be the digit "67" inside both the
                                en and ar translations — correct, and with nothing
                                keeping it correct. The day the split moves, a baked
                                digit makes this page advertise a share the platform
                                does not pay, in two languages, silently. */}
                            <div className="earnings-formula">
                                {t("billing.adSettings.earnings.body", { cafe: shares.cafe })}
                            </div>
                        </div>
                    </div>

                    {/* ── Pause ads — DELIBERATELY NOT RENDERED ────────────────
                        Hidden, not deleted. The control shipped ahead of its
                        endpoint: pause/resume is an action with its own route
                        (backend 11.7a) that does not exist yet, so pressing this
                        only ever raised "unavailable" — a button whose whole
                        function was to report that it does not work.

                        It comes back as the Talabat-style pause WITH A DURATION
                        ("pause for 1h / until tomorrow / indefinitely"), which is
                        a different control taking a different argument, not this
                        one re-enabled.

                        Everything behind it is intentionally still here — the
                        `pause` handler, `pausedAt`, .danger-zone CSS and the
                        billing.adSettings.danger.* strings. Deleting them would
                        mean rebuilding the same four pieces to ship the version
                        that replaces this.

                    <div className="danger-zone">
                        <div className="danger-zone-info">
                            <div className="danger-zone-title">{t("billing.adSettings.danger.title")}</div>
                            <div className="danger-zone-desc">{t("billing.adSettings.danger.desc")}</div>
                        </div>
                        <button type="button" className="hi-btn hi-btn-danger" onClick={pause} disabled={!!pausedAt}>
                            {t("billing.adSettings.danger.action")}
                        </button>
                    </div>
                    ─────────────────────────────────────────────────────────── */}
                </main>

                <div className="mobile-save">
                    <button type="button" className="hi-btn hi-btn-primary" onClick={save} disabled={saving}>
                        {saveLabel}
                    </button>
                </div>

                {toast && (
                    <div className="hi-toast-host" role="status" aria-live="polite">
                        <div className={`hi-toast hi-toast-${toast.severity}`}>
                            <div className="hi-toast-icon">{toast.severity === "success" ? "✓" : "!"}</div>
                            <span>{toast.text}</span>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
